import React, { useEffect, useState } from 'react';
import { ChevronDown, Download, Eye, FileText } from 'lucide-react';
import VoiceMessagePlayer from './VoiceMessagePlayer';
import MessageItem from './MessageItem';
import { downloadFile } from '../utils/downloadFile';

const URL_REGEX = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

// Renders message text with (a) URLs turned into blue clickable links and
// (b) search-term highlighting. Both are applied in one pass so a link that
// also matches the search term still highlights.
const RichText = ({ text, term, linkColor }) => {
  if (text === null || text === undefined) return null;
  const str = String(text);
  const segments = str.split(URL_REGEX);

  const highlight = (chunk, keyBase) => {
    if (!term) return chunk;
    const parts = chunk.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === term.toLowerCase() ? (
        <mark key={`${keyBase}-${i}`} style={{ background: '#fde047', color: '#1e293b', borderRadius: '3px', padding: '0 1px' }}>
          {part}
        </mark>
      ) : (
        <React.Fragment key={`${keyBase}-${i}`}>{part}</React.Fragment>
      )
    );
  };

  return segments.map((seg, i) => {
    if (!seg) return null;
    if (URL_REGEX.test(seg)) {
      URL_REGEX.lastIndex = 0; // reset — the regex is global/stateful
      const href = seg.startsWith('http') ? seg : `https://${seg}`;
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ color: linkColor || '#2563eb', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {seg}
        </a>
      );
    }
    return <React.Fragment key={i}>{highlight(seg, i)}</React.Fragment>;
  });
};

const ExpandableMessageText = ({ text, isRTL, searchTerm, sent }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;

  // Sent bubbles are dark/blue → links must be light to stay legible.
  const linkColor = sent ? '#bfdbfe' : '#2563eb';
  const isLong = text.length > 350 || (text.match(/\n/g) || []).length > 6;

  if (!isLong) {
    return (
      <p
        dir="auto"
        style={{
          margin: 0,
          wordBreak: 'break-word',
          lineHeight: '1.55',
          whiteSpace: 'pre-wrap',
          color: 'inherit',
          textAlign: 'start',
        }}
      >
        <RichText text={text} term={searchTerm} linkColor={linkColor} />
      </p>
    );
  }

  // Calculate truncated text if not expanded
  const truncatedText = isExpanded
    ? text
    : (text.length > 350 ? `${text.slice(0, 350)}...` : text.split('\n').slice(0, 6).join('\n') + '...');

  return (
    <div dir="auto" style={{ textAlign: 'start' }}>
      <p
        style={{
          margin: 0,
          wordBreak: 'break-word',
          lineHeight: '1.55',
          whiteSpace: 'pre-wrap',
          color: 'inherit',
        }}
      >
        <RichText text={truncatedText} term={searchTerm} linkColor={linkColor} />
      </p>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          opacity: 0.8,
          cursor: 'pointer',
          padding: '6px 0 0 0',
          fontSize: '13.5px',
          fontWeight: '700',
          display: 'inline-block',
        }}
      >
        {isExpanded ? (isRTL ? 'عرض أقل' : 'See less') : (isRTL ? 'قراءة المزيد' : 'Read more')}
      </button>
    </div>
  );
};

const MessagesContainer = ({
  messages,
  selectedUser,
  hasMoreMessages,
  messagesOffset,
  loadingMoreFor,
  loadMoreMessages,
  isRTL,
  c,
  employeeProfilePics,
  getAvatarColor,
  styles,
  messagesContainerRef,
  messagesEndRef,
  userAtBottomRef,
  t,
  language,
  theme,
  onDeleteMessage,
  // WhatsApp-style features
  openMediaViewer,       // (message) => void — unified lightbox, owned by MessengerPage
  onReply,
  onForward,
  onToggleStar,
  isStarredFn,           // (messageId) => boolean
  onReact,
  canViewDetails = true,
  currentUserCode,
  resolveReactorName,
  searchTerm = '',
  jumpToMessageId = null,
  onJumpHandled = () => {},
  // Optional extra class on the scroll container — e.g. the Mini Chat Dock
  // uses this to hide the scrollbar within its own scoped stylesheet without
  // touching the full page's (unaffected — this defaults to nothing extra).
  className = '',
  // Smaller image/PDF/doc card sizing for the Mini Chat Dock's narrow
  // floating window — the full page never passes this, so its rendering is
  // completely unaffected.
  compact = false,
  // Hides the sender avatar next to received bubbles — the Mini Chat Dock's
  // 1:1 windows read cleaner WhatsApp/Telegram-style with just aligned
  // bubbles; the full page (group avatars matter there) never passes this.
  hideAvatar = false,
}) => {
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [flashId, setFlashId] = useState(null);

  // Hide the scroll-to-bottom arrow when switching chats (the view is forced
  // to the latest message on every selection).
  useEffect(() => {
    setShowScrollDown(false);
  }, [selectedUser?.id]);

  // Jump to a specific message (reply-quote click, search navigation,
  // starred-messages navigation) and flash-highlight it briefly.
  useEffect(() => {
    if (!jumpToMessageId) return;
    const container = messagesContainerRef.current;
    const el = container?.querySelector(`[data-msgid="${jumpToMessageId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlashId(jumpToMessageId);
      const timer = setTimeout(() => setFlashId(null), 1600);
      onJumpHandled();
      return () => clearTimeout(timer);
    }
    onJumpHandled();
  }, [jumpToMessageId]);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    userAtBottomRef.current = distanceFromBottom < 120;
    setShowScrollDown(distanceFromBottom > 300);

    // Infinite scroll: fetch older messages as soon as the user nears the top.
    if (
      container.scrollTop < 80 &&
      container.scrollHeight > container.clientHeight &&
      hasMoreMessages[selectedUser.id] &&
      loadingMoreFor !== selectedUser.id &&
      (messages[selectedUser.id] || []).length > 0
    ) {
      loadMoreMessages(selectedUser.id);
    }
  };

  const scrollToBottom = () => {
    userAtBottomRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const renderMessageContent = (message) => {
    if (message.type === 'image') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '100%' }}>
          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '16px',
              cursor: 'pointer',
              border: message.sent ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.08)',
              background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
            }}
            onClick={() => openMediaViewer(message)}
          >
            <img
              src={message.mediaUrl}
              alt=""
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
              }}
              style={{
                maxWidth: '100%',
                maxHeight: compact ? '180px' : '350px',
                objectFit: 'contain',
                display: 'block',
              }}
            />
            <div
              style={{
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '140px',
                minWidth: '200px',
                color: message.sent ? 'rgba(255,255,255,0.8)' : c.textSecondary,
                fontSize: '13px',
                fontWeight: '600',
              }}
            >
              {isRTL ? 'تعذر تحميل الصورة' : 'Image failed to load'}
            </div>
          </div>
          {message.text && <ExpandableMessageText text={message.text} isRTL={isRTL} searchTerm={searchTerm} sent={message.sent} />}
        </div>
      );
    }

    if (message.type === 'voice') {
      return (
        <VoiceMessagePlayer
          audioUrl={message.mediaUrl}
          duration={message.duration}
          sent={message.sent}
          c={c}
          theme={theme}
          compact={compact}
        />
      );
    }

    if (message.type === 'pdf' || message.type === 'excel' || message.type === 'word' || message.type === 'powerpoint') {
      const typeColors = {
        pdf: '#ef4444',
        excel: '#10b981',
        word: '#3b82f6',
        powerpoint: '#f97316',
      };

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '100%' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: compact ? '10px' : '14px',
              // Compact (Mini Chat Dock): the bubble itself is the card — no
              // second bordered box nested inside it.
              padding: compact ? '2px 0' : '14px 16px',
              background: compact
                ? 'transparent'
                : (message.sent ? 'rgba(255,255,255,0.15)' : (theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff')),
              borderRadius: '16px',
              cursor: 'pointer',
              minWidth: compact ? '200px' : '260px',
              maxWidth: '100%',
              border: compact
                ? 'none'
                : (message.sent ? '1px solid rgba(255,255,255,0.2)' : `1px solid ${c.border}`),
            }}
            onClick={() => openMediaViewer(message)}
            title={isRTL ? 'عرض الملف' : 'Preview file'}
          >
            <div style={{ padding: compact ? '8px' : '10px', background: message.sent ? 'rgba(255,255,255,0.2)' : `${typeColors[message.type]}15`, borderRadius: '12px', flexShrink: 0 }}>
              <FileText size={compact ? 18 : 28} color={message.sent ? '#fff' : typeColors[message.type]} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: '700',
                  fontSize: compact ? '12.5px' : '14.5px',
                  marginBottom: '4px',
                  wordBreak: 'break-word',
                  color: message.sent ? '#fff' : c.text,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: '1.4',
                }}
              >
                {message.mediaName || `Document.${message.type}`}
              </div>
              <div style={{ fontSize: compact ? '11px' : '12.5px', opacity: 0.8, color: message.sent ? 'rgba(255,255,255,0.9)' : c.textSecondary, fontWeight: '500' }}>
                {t.file} • {message.mediaSize ? `${(message.mediaSize / 1024).toFixed(0)} KB` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '2px' : '6px', flexShrink: 0 }}>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  openMediaViewer(message);
                }}
                title={isRTL ? 'معاينة' : 'Preview'}
                style={{
                  padding: compact ? '6px' : '8px',
                  background: message.sent ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.04)',
                  borderRadius: '50%',
                  color: message.sent ? '#fff' : c.textSecondary,
                  cursor: 'pointer',
                }}
              >
                <Eye size={compact ? 15 : 18} />
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  downloadFile(message.mediaUrl, message.mediaName);
                }}
                title={isRTL ? 'تحميل' : 'Download'}
                style={{
                  padding: compact ? '6px' : '8px',
                  background: message.sent ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.04)',
                  borderRadius: '50%',
                  color: message.sent ? '#fff' : c.textSecondary,
                  cursor: 'pointer',
                }}
              >
                <Download size={compact ? 15 : 18} />
              </div>
            </div>
          </div>
          {message.text && <ExpandableMessageText text={message.text} isRTL={isRTL} searchTerm={searchTerm} sent={message.sent} />}
        </div>
      );
    }

    return <ExpandableMessageText text={message.text} isRTL={isRTL} searchTerm={searchTerm} sent={message.sent} />;
  };

  return (
    <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className={className}
        style={{ ...styles.messagesContainer, flex: 1 }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: compact ? '2px' : '4px',
            padding: compact ? '4px' : '8px',
          }}
        >
          {loadingMoreFor === selectedUser.id && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 14px' }}>
              <div
                style={{
                  width: '22px',
                  height: '22px',
                  border: '2.5px solid #2e7cba',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            </div>
          )}

          {(messages[selectedUser.id] || []).map((message, index) => {
            const nextMsg = index < messages[selectedUser.id].length - 1 ? messages[selectedUser.id][index + 1] : null;
            const prevMsg = index > 0 ? messages[selectedUser.id][index - 1] : null;

            // Render a date separator if the date changed
            const prevDate = prevMsg ? new Date(prevMsg.raw?.created_at || prevMsg.raw?.createdAt || prevMsg.time).toLocaleDateString() : null;
            const currDate = new Date(message.raw?.created_at || message.raw?.createdAt || message.time).toLocaleDateString();

            const showAvatar = !message.sent && (!nextMsg || nextMsg.sent || nextMsg.sender?.id !== message.sender?.id);

            return (
              <React.Fragment key={message.id}>
                {prevDate !== currDate && (compact ? (
                  <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0 8px' }}>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        color: c.textSecondary,
                        background: theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                        borderRadius: '999px',
                        padding: '3px 10px',
                      }}
                    >
                      {currDate}
                    </span>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', margin: '16px 0', fontSize: '12px', color: c.textSecondary, fontWeight: '600' }}>
                    {currDate}
                  </div>
                ))}
                {message.isSystem ? (
                  <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
                    <span
                      style={{
                        fontSize: '12.5px',
                        fontWeight: 600,
                        color: theme === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)',
                        background: theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                        borderRadius: '999px',
                        padding: '6px 14px',
                        textAlign: 'center',
                        maxWidth: '80%',
                      }}
                    >
                      {message.text}
                    </span>
                  </div>
                ) : (
                <div
                  data-msgid={message.id}
                  style={{
                    borderRadius: '14px',
                    transition: 'background 0.5s ease',
                    background: String(flashId) === String(message.id)
                      ? (theme === 'dark' ? 'rgba(46, 124, 186, 0.25)' : 'rgba(46, 124, 186, 0.14)')
                      : 'transparent',
                  }}
                >
                  <MessageItem
                    message={message}
                    selectedUser={selectedUser}
                    c={c}
                    isRTL={isRTL}
                    renderMessageContent={renderMessageContent}
                    employeeProfilePics={employeeProfilePics}
                    getAvatarColor={getAvatarColor}
                    styles={styles}
                    showAvatar={showAvatar}
                    t={t}
                    language={language}
                    theme={theme}
                    onDeleteMessage={onDeleteMessage}
                    onReply={onReply}
                    onForward={onForward}
                    onToggleStar={onToggleStar}
                    isStarred={isStarredFn ? isStarredFn(message.id) : false}
                    onReact={onReact}
                    canViewDetails={canViewDetails}
                    currentUserCode={currentUserCode}
                    resolveReactorName={resolveReactorName}
                    searchTerm={searchTerm}
                    compact={compact}
                    hideAvatar={hideAvatar}
                    onJumpToMessage={(id) => {
                      const container = messagesContainerRef.current;
                      const el = container?.querySelector(`[data-msgid="${id}"]`);
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setFlashId(id);
                        setTimeout(() => setFlashId(null), 1600);
                      }
                    }}
                  />
                </div>
                )}
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {showScrollDown && (
        <button
          onClick={scrollToBottom}
          aria-label={isRTL ? 'الانتقال لآخر رسالة' : 'Scroll to latest message'}
          title={isRTL ? 'آخر رسالة' : 'Latest message'}
          style={{
            position: 'absolute',
            bottom: '20px',
            right: isRTL ? 'auto' : '20px',
            left: isRTL ? '20px' : 'auto',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            border: `1px solid ${c.border}`,
            background: theme === 'dark' ? '#1e293b' : '#ffffff',
            color: '#2e7cba',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
            zIndex: 5,
            animation: 'fadeInScale 0.2s ease',
          }}
        >
          <ChevronDown size={22} />
        </button>
      )}
    </div>
  );
};

export default MessagesContainer;
